import dayjs from "@/utils/date";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  CheckCircle2Icon,
  DownloadIcon,
  Loader2Icon,
  Trash2Icon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  DownloadStatus,
  DownloadStatusByUuid,
  useCancelDownloadMutation,
  useDeleteDownloadMutation,
  useDownloadStatusQuery,
  useDownloadUrlLazyQuery,
} from "@/apollo/types";
import { Button, IconButton } from "@/components/elements/Button";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from "@/components/elements/HoverCard";
import { List } from "@/components/elements/List";
import {
  SlideOver,
  SlideOverBody,
  SlideOverCloseButton,
  SlideOverHeader,
} from "@/components/elements/SlideOver";
import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";
import useNow from "@/hooks/useNow";
import { useToast } from "@/providers/ToastProvider";
import { useSocketEvent } from "@/socket/SocketContext";

import { useStoredDownloads } from "./useStoredDownloads";

export type DownloadItem = {
  uuid: string;
  filename: string;
  status: DownloadStatus;
  updatedAt: string;
  expiresAt: string;
  rows?: number | null;
  error?: string | null;
};

type DownloadStatusMap = Record<string, DownloadStatusByUuid>;

const showDownloadsSidebarAtom = atom(false);
const downloadsCountAtom = atom(0);

export const useSidebarDownloads = () => {
  const [, setShowDownloads] = useAtom(showDownloadsSidebarAtom);
  const downloadsCount = useAtomValue(downloadsCountAtom);

  return useMemo(
    () => ({
      downloadsCount,
      showSidebar: () => setShowDownloads(true),
      toggleSidebar: () => setShowDownloads((value) => !value),
      hideSidebar: () => setShowDownloads(false),
    }),
    [downloadsCount, setShowDownloads],
  );
};

type SidebarDownloadsProps = {
  children?: React.ReactNode;
};

const SidebarDownloads = (props: SidebarDownloadsProps) => {
  const [showDownloads, setShowDownloads] = useAtom(showDownloadsSidebarAtom);
  const setDownloadsCount = useSetAtom(downloadsCountAtom);
  const toast = useToast();

  const { storedDownloads, removeDownloads } = useStoredDownloads();

  // Re-evaluate expiry roughly every 30s so items drop off while the panel is
  // open without needing a page reload. Pause the interval entirely while the
  // panel is closed to avoid unnecessary rerenders.
  // Use a longer interval (5m) while the panel is closed to catch any downloads that have expired in the background.
  const now = useNow(showDownloads ? 30_000 : 300_000);

  // Ignore (and don't query) downloads whose expiry has already passed.
  const activeDownloads = useMemo(
    () =>
      storedDownloads.filter((download) =>
        now.isBefore(dayjs(download.expiresAt)),
      ),
    [storedDownloads, now],
  );

  // Clean expired references out of local storage.
  useEffect(() => {
    const expiredUuids = storedDownloads
      .filter((download) => !now.isBefore(dayjs(download.expiresAt)))
      .map((download) => download.uuid);
    if (expiredUuids.length > 0) {
      removeDownloads(expiredUuids);
    }
  }, [storedDownloads, now, removeDownloads]);

  const uuids = useMemo(
    () => activeDownloads.map((download) => download.uuid),
    [activeDownloads],
  );

  const [statuses, setStatuses] = useState<DownloadStatusMap>({});

  // Websocket messages can arrive out of order (e.g. a stale "processing"
  // message after a "completed" one), so we only ever apply an incoming
  // status if it's newer than the one we already have for that uuid.
  const mergeStatuses = useCallback((items: DownloadStatusByUuid[]) => {
    setStatuses((prev) => {
      const next = { ...prev };
      for (const item of items) {
        const current = next[item.uuid];
        if (
          !current ||
          dayjs(item.updatedAt).isAfter(dayjs(current.updatedAt))
        ) {
          next[item.uuid] = item;
        }
      }
      return next;
    });
  }, []);

  // On load (and whenever the stored list changes) fetch the latest statuses
  // for every download reference we have persisted in the browser.
  const { data: statusData, variables: statusVariables } =
    useDownloadStatusQuery({
      variables: { uuids },
      skip: uuids.length === 0,
      fetchPolicy: "cache-and-network",
    });

  useEffect(() => {
    if (!statusData?.downloadStatus) return;
    mergeStatuses(statusData.downloadStatus);

    // Any uuid we asked for but the service didn't return no longer exists
    // (e.g. it was deleted server-side) – drop it from local storage too.
    // We compare against the variables of *this* response so a download that
    // was just added (and not yet part of the request) isn't pruned.
    const requested = statusVariables?.uuids;
    const requestedUuids = Array.isArray(requested)
      ? requested
      : requested != null
        ? [requested]
        : [];
    const returnedUuids = new Set(
      statusData.downloadStatus.map((download) => download.uuid),
    );
    const missingUuids = requestedUuids.filter(
      (uuid) => !returnedUuids.has(uuid),
    );
    if (missingUuids.length > 0) {
      removeDownloads(missingUuids);
    }
  }, [statusData, statusVariables, mergeStatuses, removeDownloads]);

  useSocketEvent("download:updated", {
    onMessage: (resp) => {
      mergeStatuses([resp.payload]);
    },
  });

  const [cancelDownloadMutation] = useCancelDownloadMutation();
  const [deleteDownloadMutation] = useDeleteDownloadMutation();

  const downloads = useMemo<DownloadItem[]>(
    () =>
      activeDownloads.map((stored) => {
        const status = statuses[stored.uuid];
        return {
          uuid: stored.uuid,
          filename: status?.filename ?? "Preparing download…",
          status: status?.status ?? DownloadStatus.Processing,
          updatedAt: status?.updatedAt ?? dayjs().toISOString(),
          expiresAt: status?.expiresAt ?? stored.expiresAt,
          rows: status?.rows,
          error: status?.error,
        };
      }),
    [activeDownloads, statuses],
  );

  const cancelDownloads = useCallback(
    async (uuids: string[]) => {
      if (uuids.length === 0) return;
      try {
        const { data } = await cancelDownloadMutation({
          variables: { uuids },
        });
        if (data?.cancelDownload) {
          mergeStatuses(data.cancelDownload);
        }
      } catch (error: any) {
        toast("Couldn't cancel download", error.message, "error");
      }
    },
    [cancelDownloadMutation, mergeStatuses, toast],
  );

  const deleteDownloads = useCallback(
    async (uuids: string[]) => {
      if (uuids.length === 0) return;
      try {
        await deleteDownloadMutation({ variables: { uuids } });
      } catch (error: any) {
        toast("Couldn't delete download", error.message, "error");
      }
      // Remove the references locally regardless – the user wants them gone.
      removeDownloads(uuids);
    },
    [deleteDownloadMutation, removeDownloads, toast],
  );

  const cancelAllDownloads = () => {
    const cancellable = downloads
      .filter((download) => download.status === DownloadStatus.Processing)
      .map((download) => download.uuid);
    void cancelDownloads(cancellable);
  };

  const deleteAllDownloads = () => {
    const deletable = downloads
      .filter((download) => download.status !== DownloadStatus.Processing)
      .map((download) => download.uuid);
    void deleteDownloads(deletable);
  };

  const hasProcessingDownloads = downloads.some(
    (download) => download.status === DownloadStatus.Processing,
  );
  const hasDeletableDownloads = downloads.some(
    (download) => download.status !== DownloadStatus.Processing,
  );

  // Keep the shared downloads-count atom in sync so `useSidebarDownloads()`
  // callers outside of this subtree (e.g. the nav button badge) stay
  // up-to-date without needing React context.
  useEffect(() => {
    setDownloadsCount(downloads.length);
  }, [downloads.length, setDownloadsCount]);

  return (
    <>
      {props.children}

      <SlideOver
        show={showDownloads}
        bgOverlay
        onClose={() => setShowDownloads(false)}
      >
        <SlideOverCloseButton />
        <SlideOverHeader>
          <div className="flex items-center space-x-2">
            <DownloadIcon className="size-5" />
            <span>Downloads</span>
          </div>
          <div className="flex items-center justify-end gap-2">
            {hasProcessingDownloads && (
              <Button
                onClick={cancelAllDownloads}
                size="sm"
                variant="link"
                icon={<XIcon />}
              >
                Cancel all
              </Button>
            )}
            {hasDeletableDownloads && (
              <Button
                onClick={deleteAllDownloads}
                size="sm"
                variant="link"
                icon={<Trash2Icon />}
              >
                Delete all
              </Button>
            )}
          </div>
        </SlideOverHeader>
        <SlideOverBody className="px-0">
          {downloads.length === 0 ? (
            <DownloadsEmptyState />
          ) : (
            <List as="div" role="list">
              {downloads.map((download) => (
                <DownloadRow
                  key={download.uuid}
                  download={download}
                  onCancel={() => cancelDownloads([download.uuid])}
                  onDelete={() => deleteDownloads([download.uuid])}
                />
              ))}
            </List>
          )}
        </SlideOverBody>
      </SlideOver>
    </>
  );
};

export default SidebarDownloads;

function DownloadsEmptyState() {
  return (
    <div className="my-4 flex flex-col items-center justify-center gap-4 py-10">
      <DownloadIcon className="h-16 w-16 text-gray-400" />
      <div>No downloads yet.</div>
    </div>
  );
}

function DownloadStatusIcon({ status }: { status: DownloadStatus }) {
  switch (status) {
    case DownloadStatus.Completed:
      return <CheckCircle2Icon className="size-5 text-green-500" />;
    case DownloadStatus.Pending:
    case DownloadStatus.Processing:
      return <Loader2Icon className="size-5 animate-spin text-blue-500" />;
    case DownloadStatus.Failed:
      return <XCircleIcon className="size-5 text-red-500" />;
    case DownloadStatus.Cancelled:
      return <XIcon className="size-5 text-gray-400" />;
  }
}

function DownloadStatusLabel({ download }: { download: DownloadItem }) {
  switch (download.status) {
    case DownloadStatus.Completed:
      return (
        <span className="text-xs text-muted-foreground">
          {download.rows != null
            ? `${download.rows.toLocaleString()} rows · Ready`
            : "Ready"}
        </span>
      );
    case DownloadStatus.Pending:
    case DownloadStatus.Processing:
      return (
        <span className="text-xs text-blue-500">
          Processing
          {download.rows != null
            ? ` · ${download.rows.toLocaleString()} rows`
            : ""}
        </span>
      );
    case DownloadStatus.Failed:
      return <FailedDownloadLabel error={download.error} />;
    case DownloadStatus.Cancelled:
      return <span className="text-xs text-muted-foreground">Cancelled</span>;
  }
}

// Failed error messages can be arbitrarily long, so keep the row compact by
// truncating to a single line and revealing the full message on hover.
function FailedDownloadLabel({ error }: { error?: string | null }) {
  const message = error ?? "Download failed";
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className="block max-w-full cursor-default truncate text-xs text-red-500">
          {message}
        </span>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side="bottom"
          align="start"
          className="max-h-64 w-80 overflow-y-auto text-xs wrap-break-word whitespace-pre-wrap text-red-500"
        >
          {message}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}

function DownloadRow({
  download,
  onCancel,
  onDelete,
}: {
  download: DownloadItem;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const toast = useToast();
  const isProcessing = download.status === DownloadStatus.Processing;
  const isReady = download.status === DownloadStatus.Completed;

  const [fetchDownloadUrl, { loading: isFetchingUrl }] =
    useDownloadUrlLazyQuery({ fetchPolicy: "network-only" });

  // The file url is short-lived and must be fetched fresh right before use,
  // so we resolve it on demand when the user actually wants to download the file.
  const handleDownload = useCallback(async () => {
    try {
      const { data } = await fetchDownloadUrl({
        variables: { uuid: download.uuid },
      });
      const url = data?.downloadUrl;
      if (!url) {
        toast(
          "Couldn't download file",
          "The download link is unavailable.",
          "error",
        );
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      toast("Couldn't download file", error.message, "error");
    }
  }, [download.uuid, fetchDownloadUrl, toast]);

  return (
    <List.Item role="listitem" className="gap-2">
      <List.ItemIcon className="flex items-center">
        <DownloadStatusIcon status={download.status} />
      </List.ItemIcon>
      <div className={cn("flex min-w-0 flex-1 flex-col")}>
        {isReady ? (
          <button
            type="button"
            onClick={handleDownload}
            disabled={isFetchingUrl}
            className="truncate text-left text-sm font-medium text-blue-500 hover:underline disabled:cursor-wait disabled:opacity-70"
          >
            {download.filename}
          </button>
        ) : (
          <span className="truncate text-sm font-medium">
            {download.filename}
          </span>
        )}
        <DownloadStatusLabel download={download} />
        <span className="text-1xs text-muted-foreground">
          <FormatRelativeTime date={new Date(download.updatedAt)} />
          {download.status === DownloadStatus.Completed && (
            <>
              {" · expires "}
              <FormatRelativeTime date={new Date(download.expiresAt)} />
            </>
          )}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {isReady && (
          <Tooltip content="Download">
            <IconButton
              aria-label="Download"
              size="sm"
              variant="ghost"
              onClick={handleDownload}
              disabled={isFetchingUrl}
              icon={<DownloadIcon />}
              className="p-0"
            />
          </Tooltip>
        )}
        {isProcessing ? (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : (
          <Tooltip content="Delete download">
            <IconButton
              aria-label="Delete download"
              size="sm"
              variant="ghost"
              onClick={onDelete}
              icon={<Trash2Icon />}
            />
          </Tooltip>
        )}
      </div>
    </List.Item>
  );
}
